import { useContext, useEffect, useRef } from "react";
import * as turf from "@turf/turf";
import { useParams } from "react-router-dom";
// context
import MapContext from "../../services/context/map-context";
import { useAppContext } from "../../services/context/app-context";

// utils
import {
  addClipLayer,
  addInnerPropertyLayer,
  addOrUpdateDirectionRouteLayer,
  addOrUpdateImageOverlayLayer,
  addPropertyCenterMarkerLayer,
  addPropertyDistanceLabels,
  addPropertyEdgesLayer,
  addPropertyLayer,
  addPropertyVertexLabels,
  addPropertyVertexPointLayer,
  isMapExist,
  removeAllPropertyLabels,
  removeClipLayer,
  removeDirectionRouteLayer,
  removeImageOverlayLayer,
  removeInnerPropertyLayer,
  removePropertyCenterMarkerLayer,
  removePropertyEdgesLayer,
  removePropertyLayer,
  removePropertyVertexPointLayer,
} from "../../utils/map-layer";
import { calculateOffsetPolygon } from "../../utils/map-property-border";
import {
  createDistanceAndVertexFeatures,
  polygonToLines,
} from "../../utils/map-polygon";
import { fetchRoute } from "../../utils/map-direction";
import { getImageOverlayCorners } from "../../utils/image-corners";

// constants
import {
  DEV_OUTLINEs,
  DEV_PROJECT_IDs,
  DEV_ROUTE_COORDINATES_1,
  DEV_ROUTE_COORDINATES_2,
  DEV_ROUTE_COORDINATES_3,
  PROD_OUTLINEs,
  PROD_PROJECT_IDs,
} from "../../constants/global.constants";

export const PropertyLayer = ({
  runnerPropertyData,
  displayDimensions,
  selectedRunner,
}) => {
  const { projectId } = useParams();
  const { map, mapLoaded } = useContext(MapContext);
  const { property, showBaseBuildings } = useAppContext();

  const PROJECT_IDs =
    process.env.NODE_ENV === "production" ? PROD_PROJECT_IDs : DEV_PROJECT_IDs;
  const OUTLINES =
    process.env.NODE_ENV === "production" ? PROD_OUTLINEs : DEV_OUTLINEs;

  const propertyGeoData = runnerPropertyData?.geodata
    ? {
        type: "FeatureCollection",
        features: [JSON.parse(runnerPropertyData.geodata)],
      }
    : property.propertyGeoJSON;

  const selectedLine = runnerPropertyData?.access1
    ? JSON.parse(runnerPropertyData.access1)
    : property.selectedLine;

  const minBorderDistance = runnerPropertyData
    ? runnerPropertyData.minBorderDistance
    : property.minBorderDistance;

  const location = runnerPropertyData
    ? runnerPropertyData.location
    : property.location;

  /*   const calculateOffsetPolygon = (polygonGeoJSON, borderWidth) => {
    if (!polygonGeoJSON || !borderWidth) return null;
    const negativeBufferDistance = -borderWidth; // Negative to shrink the polygon
    try {
      const offset = turf.buffer(
        polygonGeoJSON.features[0],
        negativeBufferDistance, // negative => shrink
        { units: "meters" }
      );
      return offset;
    } catch (error) {
      console.error("Error calculating offset polygon:", error);
      return null;
    }
  }; */

  // Add or update the property layer when the geo data changes.
  useEffect(() => {
    if (map && mapLoaded && propertyGeoData) {
      addProperty(propertyGeoData);
    }

    // Cleanup on unmount
    return () => {
      removeProperty(map);
    };
  }, [map, mapLoaded, runnerPropertyData]);

  useEffect(() => {
    if (map && mapLoaded && projectId && PROJECT_IDs.includes(projectId)) {
      PROJECT_IDs.forEach((pid) => {
        if (projectId === pid) return;
        const idPrefix = pid;
        const outline = OUTLINES[pid];
        const extraPropertyGeoData = outline?.geodata
          ? {
              type: "FeatureCollection",
              features: [JSON.parse(outline.geodata)],
            }
          : null;

        const extraSelectedLine = outline?.access1
          ? JSON.parse(outline.access1)
          : property.selectedLine;

        const extraMinBorderDistance = outline.minBorderDistance;

        const extraLocation = outline.location ?? "";

        if (extraPropertyGeoData) {
          addExtraProperty(
            extraPropertyGeoData,
            extraSelectedLine,
            extraMinBorderDistance,
            extraLocation,
            idPrefix,
          );
        }
      });

      displayRoutes();

      const corners = getImageOverlayCorners(
        [14.304142557841416, 48.28919558569908],
        80,
        80,
        -35,
      );

      addOrUpdateImageOverlayLayer(
        map,
        "",
        corners,
        /* [
    [14.303081, 48.289889], // top-left
    [-9.140, 38.726], // top-right
    [-9.140, 38.725], // bottom-right
    [-9.142, 38.725], // bottom-left
  ]*/ "main",
        { opacity: 0.7 /* maxzoom: 16 */ },
      );
    }

    // Cleanup on unmount
    return () => {
      if (isMapExist(map)) {
        if (projectId && PROJECT_IDs.includes(projectId)) {
          PROJECT_IDs.forEach((pid) => {
            if (projectId === pid) return;
            const idPrefix = pid;

            removeExtraProperty(map, idPrefix);
          });
        }
      }
    };
  }, [map, mapLoaded, projectId]);

  // Display (or remove) labels if displayDimensions is toggled.
  useEffect(() => {
    if (map && mapLoaded && propertyGeoData && displayDimensions) {
      addPropertyLabels(propertyGeoData);
    }

    if (map && mapLoaded && !displayDimensions) {
      removeAllPropertyLabels(map);
      removePropertyVertexPointLayer(map);
    }

    // Cleanup on unmount
    return () => {
      if (isMapExist(map)) {
        removeAllPropertyLabels(map);
      }
    };
  }, [map, mapLoaded, runnerPropertyData, displayDimensions]);

  // hide base buildings
  useEffect(() => {
    if ((!showBaseBuildings && propertyGeoData) || selectedRunner) {
      const propertyFeature = propertyGeoData.features[0];
      addClipLayer(map, propertyFeature);
    }
    if (showBaseBuildings && !selectedRunner) {
      removeClipLayer(map);
    }
  }, [showBaseBuildings, selectedRunner]);

  const addProperty = (propertyGeoData) => {
    const propertyFeature = propertyGeoData.features[0];

    // Load and add the hatched-line image if not already available.
    addPropertyLayer(map, propertyFeature);

    // Add the eraser/clip layer
    if (!showBaseBuildings || selectedRunner) {
      addClipLayer(map, propertyFeature);
    }

    // Fit the map to the property
    const centroid = turf.centroid(propertyFeature);
    const coordinates = centroid.geometry.coordinates;

    map.flyTo({ center: coordinates, zoom: 16 });

    // add marker for this property
    addPropertyCenterMarkerLayer(map, coordinates, location ?? "");

    // Add offset polygon (zone) if a minBorderDistance is specified.
    const offsetPolygon = calculateOffsetPolygon(
      propertyGeoData,
      minBorderDistance,
    );

    if (offsetPolygon) {
      addInnerPropertyLayer(map, offsetPolygon);
    }

    // Add the edges layer.
    addEdgesAndEntrySide(propertyFeature, selectedLine);
  };

  const addEdgesAndEntrySide = (
    propertyFeature,
    selectedLine,
    idPrefix = "main",
  ) => {
    if (!propertyFeature) return;
    const lineFeatures = polygonToLines(propertyFeature);
    const edgesGeoData = {
      type: "FeatureCollection",
      features: lineFeatures,
    };

    addPropertyEdgesLayer(map, edgesGeoData, selectedLine, idPrefix);
  };

  const addPropertyLabels = (propertyGeoData) => {
    const propertyFeature = propertyGeoData.features[0];
    const { distanceFeatures, vertexFeatures } =
      createDistanceAndVertexFeatures(propertyFeature);
    const distanceGeoData = {
      type: "FeatureCollection",
      features: distanceFeatures,
    };
    const vertexGeoData = {
      type: "FeatureCollection",
      features: vertexFeatures,
    };
    const explodedFC = turf.explode(propertyFeature);
    addPropertyVertexPointLayer(map, explodedFC);
    addPropertyDistanceLabels(map, distanceGeoData);
    addPropertyVertexLabels(map, vertexGeoData);
  };

  const removeProperty = (map) => {
    if (!isMapExist(map)) return;
    // Remove all related layers & sources
    removePropertyLayer(map);
    removeClipLayer(map);
    removeInnerPropertyLayer(map);
    removePropertyEdgesLayer(map);
    removeAllPropertyLabels(map);
    removePropertyVertexPointLayer(map);
    removePropertyCenterMarkerLayer(map);
  };

  const addExtraProperty = (
    propertyGeoData,
    extraSelectedLine,
    extraMinBorderDistance,
    extraLocation,
    idPrefix,
  ) => {
    const extraPropertyFeature = propertyGeoData.features[0];

    // Load and add the hatched-line image if not already available.
    addPropertyLayer(map, extraPropertyFeature, idPrefix);

    /* // Add the eraser/clip layer
    if (!showBaseBuildings || selectedRunner) {
      addClipLayer(map, propertyFeature);
    }*/

    // Fit the map to the property
    const centroid = turf.centroid(extraPropertyFeature);
    const coordinates = centroid.geometry.coordinates;

    // add marker for this property
    addPropertyCenterMarkerLayer(
      map,
      coordinates,
      extraLocation ?? "",
      idPrefix,
    );

    // Add offset polygon (zone) if a minBorderDistance is specified.
    const extraOffsetPolygon = calculateOffsetPolygon(
      propertyGeoData,
      extraMinBorderDistance,
    );

    if (extraOffsetPolygon) {
      addInnerPropertyLayer(map, extraOffsetPolygon, idPrefix);
    }

    // Add the edges layer.
    if (extraSelectedLine) {
      addEdgesAndEntrySide(extraPropertyFeature, extraSelectedLine, idPrefix);
    }
  };

  const removeExtraProperty = (map, idPrefix) => {
    // Remove all related layers & sources
    removePropertyLayer(map, idPrefix);
    removeInnerPropertyLayer(map, idPrefix);
    removePropertyEdgesLayer(map, idPrefix);
    removePropertyCenterMarkerLayer(map, idPrefix);
    removeDirectionRouteLayer(map, "_1");
    removeDirectionRouteLayer(map, "_2");
    removeDirectionRouteLayer(map, "_3");
    removeImageOverlayLayer(map);
  };

  const displayRoutes = async () => {
    const routeFeature_1 = await fetchRoute(DEV_ROUTE_COORDINATES_1, "driving");
    const routeFeature_2 = await fetchRoute(DEV_ROUTE_COORDINATES_2, "driving");
    const routeFeature_3 = await fetchRoute(DEV_ROUTE_COORDINATES_3, "driving");

    addOrUpdateDirectionRouteLayer(map, routeFeature_1, "_1");
    addOrUpdateDirectionRouteLayer(map, routeFeature_2, "_2");
    addOrUpdateDirectionRouteLayer(map, routeFeature_3, "_3");
  };

  return null;
};
